// Info anime (poster, banner, sinopsis, genre, skor) dari AniList
// (https://anilist.co, API GraphQL publik tanpa kunci). Yang dikirim ke
// AniList hanya judul pencarian (nama folder seri yang sudah dibersihkan).
// Hasilnya disimpan di localStorage 30 hari supaya tidak meminta ulang.
const ENDPOINT = 'https://graphql.anilist.co';
const CACHE_KEY = 'disbox_anilist_cache';
const OVERRIDE_KEY = 'disbox_anilist_override';
const ENABLED_KEY = 'disbox_anilist_enabled';
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

const QUERY = `query ($search: String, $id: Int) {
  Media(search: $search, id: $id, type: ANIME, sort: SEARCH_MATCH) {
    id
    title { romaji english native }
    coverImage { extraLarge large color }
    bannerImage
    description(asHtml: false)
    genres
    averageScore
    episodes
    seasonYear
    format
    status
    siteUrl
  }
}`;

function readJson(key) {
    try {
        return JSON.parse(localStorage.getItem(key)) || {};
    } catch {
        return {};
    }
}

function writeJson(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // localStorage penuh: info diambil ulang lain kali.
    }
}

export function isAnilistEnabled() {
    try {
        return localStorage.getItem(ENABLED_KEY) !== '0';
    } catch {
        return true;
    }
}

export function setAnilistEnabled(enabled) {
    try {
        localStorage.setItem(ENABLED_KEY, enabled ? '1' : '0');
    } catch {
        // Abaikan.
    }
}

// Judul pencarian khusus per seri (kalau hasil otomatis salah).
export function getSearchOverride(seriesId) {
    return readJson(OVERRIDE_KEY)[seriesId] || '';
}

export function setSearchOverride(seriesId, text) {
    const all = readJson(OVERRIDE_KEY);
    if (text) all[seriesId] = text;
    else delete all[seriesId];
    writeJson(OVERRIDE_KEY, all);
}

function cleanDescription(text) {
    return String(text || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\(Source:[^)]*\)/gi, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

const inFlight = new Map();
let queue = Promise.resolve();

// Permintaan dijalankan berurutan dengan jeda kecil (batas AniList ~30/menit).
function throttled(task) {
    const run = queue.then(task, task);
    queue = run.then(() => new Promise((r) => setTimeout(r, 700)), () => new Promise((r) => setTimeout(r, 700)));
    return run;
}

// Hasil: objek info, atau null kalau tidak ketemu / dimatikan / offline.
export async function fetchAnimeInfo(search) {
    if (!search || !isAnilistEnabled()) return null;
    const key = search.toLowerCase();
    const cache = readJson(CACHE_KEY);
    const cached = cache[key];
    if (cached && Date.now() - cached.at < MAX_AGE) return cached.info;
    if (inFlight.has(key)) return inFlight.get(key);
    const request = throttled(async () => {
        const isId = /^\d+$/.test(search.trim());
        const response = await fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ query: QUERY, variables: isId ? { id: Number(search) } : { search } }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok && response.status !== 404) throw new Error(`AniList ${response.status}`);
        const media = data && data.data && data.data.Media;
        const info = media ? {
            id: media.id,
            title: media.title.romaji || media.title.english || search,
            titleEnglish: media.title.english || null,
            titleNative: media.title.native || null,
            cover: media.coverImage.extraLarge || media.coverImage.large || null,
            color: media.coverImage.color || null,
            banner: media.bannerImage || null,
            description: cleanDescription(media.description),
            genres: media.genres || [],
            score: media.averageScore || null,
            episodes: media.episodes || null,
            year: media.seasonYear || null,
            format: media.format || null,
            status: media.status || null,
            siteUrl: media.siteUrl || null,
        } : null;
        const fresh = readJson(CACHE_KEY);
        fresh[key] = { at: Date.now(), info };
        writeJson(CACHE_KEY, fresh);
        return info;
    }).catch(() => (cached ? cached.info : null)).finally(() => inFlight.delete(key));
    inFlight.set(key, request);
    return request;
}

const SEARCH_QUERY = `query ($search: String) {
  Page(perPage: 6) {
    media(search: $search, type: ANIME, sort: SEARCH_MATCH) {
      id
      title { romaji english }
      coverImage { large color }
      episodes
      seasonYear
      format
    }
  }
}`;

// Daftar beberapa anime yang cocok (untuk kolom pencarian judul di halaman
// kontribusi). Tidak disimpan di cache.
export async function searchAnime(text, signal) {
    const search = String(text || '').trim();
    if (search.length < 2) return [];
    const response = await throttled(() => fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: SEARCH_QUERY, variables: { search } }),
        signal,
    }));
    const data = await response.json().catch(() => ({}));
    const list = (data && data.data && data.data.Page && data.data.Page.media) || [];
    return list.map((m) => ({
        id: m.id,
        title: m.title.romaji || m.title.english,
        titleEnglish: m.title.english || null,
        cover: m.coverImage.large || null,
        color: m.coverImage.color || null,
        episodes: m.episodes || null,
        year: m.seasonYear || null,
        format: m.format || null,
    }));
}

export function clearAnimeInfoCache(search) {
    const cache = readJson(CACHE_KEY);
    if (search) delete cache[search.toLowerCase()];
    else Object.keys(cache).forEach((k) => delete cache[k]);
    writeJson(CACHE_KEY, cache);
}

export const FORMAT_LABELS = { TV: 'TV', TV_SHORT: 'TV Pendek', MOVIE: 'Movie', SPECIAL: 'Spesial', OVA: 'OVA', ONA: 'ONA', MUSIC: 'Musik' };
export const STATUS_LABELS = { FINISHED: 'Tamat', RELEASING: 'Tayang', NOT_YET_RELEASED: 'Belum tayang', CANCELLED: 'Dibatalkan', HIATUS: 'Hiatus' };
